/**
 * The L2 response cache — `/v1/admin/response-cache`, arch-14 §6.
 *
 * **Any scope reads; only superadmin flushes.** The state is shared ops context — "is the cache
 * why this page still shows the old price?" — while a flush sends every public read to MySQL
 * until the cache refills, so the server holds it to the scope that owns incidents and audits the
 * reason. The console mirrors neither rule; it sends the reason and shows a refusal as sent.
 *
 * **A flush is a version bump, not a delete.** Every key embeds the version, so the old keys stop
 * being read at once and expire on their own TTL. That is why the answer is two numbers.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type ResponseCacheState = components['schemas']['ResponseCacheState'];
type ResponseCacheFlushed = components['schemas']['ResponseCacheFlushed'];

export function useResponseCacheState() {
  return useQuery({
    queryKey: keys.responseCache.state(),
    queryFn: () => authedClient.request<ResponseCacheState>('/v1/admin/response-cache'),
  });
}

export function useFlushResponseCache() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) =>
      authedClient.request<ResponseCacheFlushed>('/v1/admin/response-cache/flush', {
        method: 'POST',
        body: { reason },
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.responseCache.state() }),
  });
}
